import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/AuthContext";
import { formatDateTime } from "../lib/format";
import {
  ACTION_LABELS,
  ENTITY_LABELS,
  diffSnapshots,
  entityLabel,
  fetchAuditActors,
  fetchAuditPage,
  type AuditAction,
  type AuditActor,
  type AuditEntry,
  type AuditRefs,
  type AuditFilter,
  type FieldChange,
} from "../lib/audit";

/**
 * Экран «Журнал аудита» (E2). Только для юриста и админа: сервер проверяет право `audit:view`
 * на каждом маршруте (permissions.ts, вне таблицы 4.3 — основание там); навигация лишь скрывает
 * раздел (правило 5). Оформление — токены docs/07 (правило 9), diff-подсветка верификации Д-4.
 *
 * СТРОГО на чтение: журнал append-only (ТЗ 4.1.2, Р-9) — ни одной кнопки правки/удаления записи
 * здесь нет и быть не может. Только фильтры (объект/пользователь/период/действие) и пагинация.
 * Момент действия показывается в поясе организации (Омск, Р-16), «было/стало» — построчным diff.
 */

const PAGE_SIZE = 50;

/**
 * Типы объектов для фильтра (термины ТЗ). Значения — КОДЫ сущностей аудита, ровно как их пишет
 * сервер: фильтр уходит в запрос как есть, поэтому опечатка в коде даёт «ничего не найдено».
 * Коды и подписи берём из общей карты `ENTITY_LABELS` (она сверяется автотестом с сервером).
 *
 * D-52: список строится из карты, а не переписан здесь. Раньше тут стояли девять кодов руками,
 * и три типа, добавленные в карту при D-49 (черновик разбора, ставка ЦБ, день календаря), в
 * журнале подписывались, а в фильтре их не было. Новый код в карте теперь сам попадает в фильтр.
 */
const ENTITY_OPTIONS: { readonly value: string; readonly label: string }[] = Object.entries(
  ENTITY_LABELS,
).map(([value, label]) => ({ value, label }));

const ACTION_OPTIONS: { readonly value: AuditAction; readonly label: string }[] = [
  { value: "create", label: "Создание" },
  { value: "update", label: "Изменение" },
  { value: "delete", label: "Удаление" },
];

/** Черновик формы фильтра (строки полей ввода). Применяется по кнопке «Показать». */
interface FilterDraft {
  entity: string;
  actorId: string;
  action: string;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_DRAFT: FilterDraft = { entity: "", actorId: "", action: "", dateFrom: "", dateTo: "" };

type LoadState =
  | { readonly kind: "loading" }
  | {
      readonly kind: "ready";
      readonly items: AuditEntry[];
      readonly total: number;
      /** Имена для ссылочных полей снимков — приходят вместе со страницей (E17, 18.09.2026). */
      readonly refs: AuditRefs;
    }
  | { readonly kind: "error"; readonly message: string };

const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";

/** Штамп действия: создание — «done», изменение — нейтральный, удаление — «overdue». */
function ActionStamp({ action }: { action: AuditAction }) {
  const cls =
    action === "create"
      ? "text-done-fg bg-done-bg"
      : action === "delete"
        ? "text-overdue-fg bg-overdue-bg"
        : "text-control-fg bg-control-bg";
  return <span className={`stamp ${cls}`}>{ACTION_LABELS[action]}</span>;
}

/** Построчный diff «было → стало» — только изменившиеся поля (Д-4: diff-подсветка). */
function ChangeView({ entry, refs }: { entry: AuditEntry; refs: AuditRefs }) {
  // Словари имён приходят со страницей журнала: ссылочные поля показываются человеком и
  // контрагентом, а не идентификаторами (18.09.2026, шаг 12 чек-листа Б11).
  const changes = diffSnapshots(entry.before, entry.after, entry.entity, refs);
  if (changes.length === 0) {
    return <span className="text-[13px] text-muted">без изменений полей</span>;
  }
  return (
    <ul className="space-y-1">
      {changes.map((c) =>
        c.summary === undefined ? (
          <li key={c.field} className="text-[13px]">
            <span className="text-muted">{c.label}: </span>
            <BeforeAfter change={c} />
          </li>
        ) : (
          <CollapsedChange
            key={c.field}
            change={c}
            summary={c.summary}
            detailsId={`audit-${entry.id}-${c.field}`}
          />
        ),
      )}
    </ul>
  );
}

function BeforeAfter({ change }: { change: FieldChange }) {
  // Построчно — только контактные лица (перечень владельца). Иной текст с переводами строк
  // (причина из многострочного поля) показывается, как и раньше, одной строкой.
  const byLines = change.field === "contacts";
  return (
    <>
      {change.before !== null && (
        <Value
          text={change.before}
          byLines={byLines}
          className="rounded bg-overdue-bg px-1 text-overdue-fg line-through"
        />
      )}
      {change.before !== null && change.after !== null && <span className="text-muted"> → </span>}
      {change.after !== null && (
        <Value
          text={change.after}
          byLines={byLines}
          className="rounded bg-done-bg px-1 text-done-fg"
        />
      )}
    </>
  );
}

/**
 * Значение «было» или «стало». Контактные лица контрагента (D-51, дополнение 2 владельца
 * 13.09.2026) показываются каждое своей строкой, одна под другой, а блок целиком стоит рядом со
 * стрелкой.
 */
function Value({
  text,
  className,
  byLines,
}: {
  text: string;
  className: string;
  byLines: boolean;
}) {
  const lines = text.split("\n");
  if (!byLines || lines.length === 1) return <span className={className}>{text}</span>;
  return (
    <span className={`inline-block align-top ${className}`}>
      {lines.map((line, i) => (
        <span key={i} className="block">
          {line}
        </span>
      ))}
    </span>
  );
}

/**
 * Поле, которое целиком в журнал не помещается, — «Поля черновика» (D-51, дополнение владельца
 * 13.09.2026). Свёрнуто до «изменено: …», по клику — полное «было → стало».
 *
 * Перенос «где угодно» (`overflow-wrap: anywhere`) у раскрытого значения обязателен: это разбор
 * договора одной строкой JSON без пробелов, и без него он раздвигает таблицу журнала в ширину —
 * ровно то, от чего избавлялись в CR-5. `break-all` не взят: он рвёт и обычные слова, когда
 * места хватает. Ширина проверена вручную на 1366×768 (13.09.2026), автопроверки нет.
 */
function CollapsedChange({
  change,
  summary,
  detailsId,
}: {
  change: FieldChange;
  summary: string;
  detailsId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="text-[13px]">
      <span className="text-muted">{change.label}: </span>
      <span className="text-ink">{summary}</span>{" "}
      <button
        type="button"
        className="text-[13px] font-bold text-accent underline-offset-2 hover:underline"
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Свернуть" : "Показать полностью"}
      </button>
      {open && (
        <div id={detailsId} className="mt-1 [overflow-wrap:anywhere]">
          <BeforeAfter change={change} />
        </div>
      )}
    </li>
  );
}

export function AuditPage() {
  const { logout } = useAuth();
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  // Применённый фильтр (по нему грузим). Отделён от черновика: печатать в полях можно свободно,
  // запрос уходит только по «Показать» — журнал большой, дёргать сервер на каждый символ ни к чему.
  const [applied, setApplied] = useState<FilterDraft>(EMPTY_DRAFT);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [actors, setActors] = useState<AuditActor[]>([]);

  const filter = useMemo<AuditFilter>(
    () => ({
      entity: applied.entity || undefined,
      actorId: applied.actorId || undefined,
      action: (applied.action as AuditAction) || undefined,
      dateFrom: applied.dateFrom || undefined,
      dateTo: applied.dateTo || undefined,
      page,
      limit: PAGE_SIZE,
    }),
    [applied, page],
  );

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchAuditPage(filter);
    if (res.ok) {
      setState({
        kind: "ready",
        items: res.value.items,
        total: res.value.total,
        refs: res.value.refs,
      });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [filter, logout]);

  useEffect(() => {
    void load();
  }, [load]);

  // Список авторов для фильтра — грузим один раз (отдельный маршрут: юристу /api/users закрыт).
  useEffect(() => {
    void fetchAuditActors().then((res) => {
      if (res.ok) setActors(res.value);
    });
  }, []);

  const applyFilter = () => {
    setPage(1);
    setApplied(draft);
  };
  const resetFilter = () => {
    setPage(1);
    setDraft(EMPTY_DRAFT);
    setApplied(EMPTY_DRAFT);
  };

  const set = (patch: Partial<FilterDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  const total = state.kind === "ready" ? state.total : 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section aria-labelledby="audit-title">
      <h1 id="audit-title" className="text-[20px] font-bold text-ink">
        Журнал аудита
      </h1>
      <p className="mt-1 max-w-[var(--w-text)] text-[13px] text-muted">
        Кто, когда и что изменил: каждая правовая операция фиксируется автоматически. Записи только
        просматриваются — журнал не редактируется и не удаляется (имеет доказательное значение).
        Время — по Омску.
      </p>

      <FilterBar
        draft={draft}
        actors={actors}
        onChange={set}
        onApply={applyFilter}
        onReset={resetFilter}
      />

      <div className="mt-4">
        {state.kind === "loading" && (
          <p className="text-muted" role="status">
            Загрузка журнала…
          </p>
        )}

        {state.kind === "error" && (
          <div
            role="alert"
            className="rounded-[var(--radius-l)] border border-border bg-surface p-5"
          >
            <p className="text-overdue-fg">{state.message}</p>
            <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
              Повторить
            </button>
          </div>
        )}

        {state.kind === "ready" && (
          <>
            <AuditTable items={state.items} refs={state.refs} />
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              onPage={setPage}
              disabled={false}
            />
          </>
        )}
      </div>
    </section>
  );
}

/** Панель фильтров: объект, пользователь, действие, период. */
function FilterBar({
  draft,
  actors,
  onChange,
  onApply,
  onReset,
}: {
  draft: FilterDraft;
  actors: AuditActor[];
  onChange: (patch: Partial<FilterDraft>) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <form
      aria-label="Фильтры журнала"
      className="mt-4 rounded-[var(--radius-l)] border border-border bg-surface p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="f-entity" className="mb-1 block text-[13px] text-muted">
            Объект
          </label>
          <select
            id="f-entity"
            value={draft.entity}
            onChange={(e) => onChange({ entity: e.target.value })}
            className={inputCls}
          >
            <option value="">Все объекты</option>
            {ENTITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-actor" className="mb-1 block text-[13px] text-muted">
            Пользователь
          </label>
          <select
            id="f-actor"
            value={draft.actorId}
            onChange={(e) => onChange({ actorId: e.target.value })}
            className={inputCls}
          >
            <option value="">Все пользователи</option>
            <option value="system">Система (фоновые операции)</option>
            {actors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-action" className="mb-1 block text-[13px] text-muted">
            Действие
          </label>
          <select
            id="f-action"
            value={draft.action}
            onChange={(e) => onChange({ action: e.target.value })}
            className={inputCls}
          >
            <option value="">Любое</option>
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="f-from" className="mb-1 block text-[13px] text-muted">
              С даты
            </label>
            <input
              id="f-from"
              type="date"
              value={draft.dateFrom}
              onChange={(e) => onChange({ dateFrom: e.target.value })}
              className={`num ${inputCls}`}
            />
          </div>
          <div>
            <label htmlFor="f-to" className="mb-1 block text-[13px] text-muted">
              По дату
            </label>
            <input
              id="f-to"
              type="date"
              value={draft.dateTo}
              onChange={(e) => onChange({ dateTo: e.target.value })}
              className={`num ${inputCls}`}
            />
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={btnPrimary}>
          Показать
        </button>
        <button type="button" onClick={onReset} className={btnSecondary}>
          Сбросить
        </button>
      </div>
    </form>
  );
}

/** Таблица записей журнала: когда, кто, объект, действие, было/стало. */
function AuditTable({ items, refs }: { items: AuditEntry[]; refs: AuditRefs }) {
  if (items.length === 0) {
    return <p className="text-muted">По заданным условиям записей нет.</p>;
  }
  return (
    <div className="min-w-fit rounded-[var(--radius-l)] border border-border bg-surface">
      <table className="sticky-head w-full border-collapse text-[14px]">
        <caption className="sr-only">Записи журнала аудита</caption>
        <thead>
          <tr className="border-b border-border text-left text-[12px] text-muted">
            <th scope="col" className="px-4 py-2.5 font-bold">
              Когда
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Кто
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Логин
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Объект
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Действие
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Было / стало
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((entry) => (
            <tr key={entry.id} className="border-b border-border-soft align-top last:border-b-0">
              <td className="num whitespace-nowrap px-4 py-3 text-[13px] text-ink">
                {formatDateTime(entry.at)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {entry.actor ? (
                  <span className="text-ink">{entry.actor.fullName}</span>
                ) : (
                  <span className="text-[13px] text-muted">Система</span>
                )}
              </td>
              <td className="num px-4 py-3 whitespace-nowrap text-[12px] text-muted">
                {entry.actor ? entry.actor.login : "—"}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-[13px] text-ink">
                {entityLabel(entry.entity)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <ActionStamp action={entry.action} />
              </td>
              <td className="px-4 py-3">
                <ChangeView entry={entry} refs={refs} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Постраничная навигация: «предыдущая/следующая» + счётчик. */
function Pagination({
  page,
  totalPages,
  total,
  onPage,
  disabled,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (p: number) => void;
  disabled: boolean;
}) {
  if (total === 0) return null;
  return (
    <div className="mt-3 flex items-center justify-between text-[13px] text-muted">
      <span>
        Всего записей: <span className="num text-ink">{total}</span>
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btnSecondary}
          disabled={disabled || page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Назад
        </button>
        <span className="num">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          className={btnSecondary}
          disabled={disabled || page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Вперёд
        </button>
      </div>
    </div>
  );
}
